/**
 * authStallVital — how a page says its sign-in start was held up, and by what (2026-10-03).
 *
 * Firebase checks its own browser database when it boots. While another prest0 tab has a delete of that database
 * waiting (the sign-out wipe of an older build did that), every later open of it queues behind the delete, and the
 * page's sign-in start waits until every prest0 tab is closed: Justin's stuck "getting your threads back…". The
 * startup watch (authStartupWatch.ts) stamps what it found once per page load as a User Timing mark, and the
 * first-party web-vitals beacon (src/reportWebVitals.ts) sends it as one more row: a RUNG from the fixed list below
 * and how long the page waited for sign-in to answer. Nothing else.
 *
 * EVERY SIGN-IN'S TIME, AND A WAIT MEASURED FROM ITS OWN START (2026-10-31, the sign-in stalls). A stall row's value
 * was the time from the page's start to sign-in's answer, and 39 of the week's 59 read under 10 s although a row is
 * only stamped once the watch's own 10 s ran out, so that value did not say how long sign-in itself took. The watch now
 * stamps when sign-in started, and both rows measure from that mark. AUTH_READY, sent for every page view whose
 * sign-in answered while it was on screen, is how long that took and whether it found someone: the distribution the
 * stall row alone could never give.
 *
 * A zero-import leaf on purpose: the watch stamps from the first download of every page. Reading the stamps back is
 * ./authStallRead.ts, beside the reporter's own download, so the first download carries only the writing. The server's
 * allow-list (functions/src/telemetry/webVitalsSchema.ts) carries the same literals; tests/auth/authStartupWatch.test.ts
 * pins the pair.
 */

/** The User Timing mark the watch stamps once per page load. */
export const AUTH_STALL_MARK = 'prest0:auth-stall';
/** Stamped once, when sign-in first answers (who is here, or that nobody is). */
export const AUTH_ANSWERED_MARK = 'prest0:auth-answered';
/** Stamped once, when the watch starts: Firebase's auth instance has just been created. */
export const AUTH_STARTED_MARK = 'prest0:auth-started';

/** The metric name the beacon sends the mark under. */
export const AUTH_STALL_METRIC = 'AUTH_STALL';

/** The metric for every page view's sign-in: how long it took to answer, and whether it found someone. */
export const AUTH_READY_METRIC = 'AUTH_READY';

/** Every outcome an AUTH_READY row may carry, and nothing else. */
export const AUTH_READY_RUNGS = ['signed-in', 'signed-out'] as const;

export type AuthReadyRung = (typeof AUTH_READY_RUNGS)[number];

/**
 * Every outcome the watch can report, and nothing else.
 *   auth-db       Firebase's sign-in database could not be opened in time (another tab's pending delete)
 *   heartbeat-db  Firebase's heartbeat database could not be opened in time
 *   unheld        sign-in had not answered, and no database this boot opens was held: the network, or a page too
 *                 busy to hear the answer
 */
export const AUTH_STALL_RUNGS = ['auth-db', 'heartbeat-db', 'unheld'] as const;

export type AuthStallRung = (typeof AUTH_STALL_RUNGS)[number];

/** The longest wait a row carries (the server's own ceiling for any value). */
export const AUTH_STALL_MAX_MS = 120_000;

export type MarkingPerformance = Performance & {
  mark?: Performance['mark'];
  getEntriesByName?: Performance['getEntriesByName'];
};

/** The page's User Timing, or null where there is none. Never throws. */
export function markingPerformance(): MarkingPerformance | null {
  try {
    return typeof performance !== 'undefined' ? (performance as MarkingPerformance) : null;
  } catch {
    return null;
  }
}

function hasMark(name: string): boolean {
  try {
    return (markingPerformance()?.getEntriesByName?.(name, 'mark')?.length ?? 0) > 0;
  } catch {
    return false;
  }
}

/** Stamp what the watch found, once per page load. Nothing here can throw into the page. */
export function stampAuthStall(rung: AuthStallRung): void {
  if (hasMark(AUTH_STALL_MARK)) return;
  try {
    markingPerformance()?.mark?.(AUTH_STALL_MARK, { detail: { rung } });
  } catch {
    /* measurement must never affect the page it measures */
  }
}

/** Stamp the moment sign-in starts, once per page load. */
export function stampAuthStarted(): void {
  if (hasMark(AUTH_STARTED_MARK)) return;
  try {
    markingPerformance()?.mark?.(AUTH_STARTED_MARK);
  } catch {
    /* measurement must never affect the page it measures */
  }
}

/** Stamp the moment sign-in first answered, and whether it found someone, once per page load. */
export function stampAuthAnswered(signedIn?: boolean): void {
  if (hasMark(AUTH_ANSWERED_MARK)) return;
  try {
    markingPerformance()?.mark?.(AUTH_ANSWERED_MARK, { detail: { signedIn: typeof signedIn === 'boolean' ? signedIn : null } });
  } catch {
    /* measurement must never affect the page it measures */
  }
}
